import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  PROMO_CODE_VERIFICATION_STATUS,
  promoCodeType,
} from '../../features/promotions/types/promotionTypes';
import { usePromotionsContext } from '../../hooks/usePromotionsContext';
import promotionCoupon from '../../images/promotion-coupon.svg';

interface IPromoCodeWidgetMobileProps {
  availablePromoCodes: promoCodeType[];
}

const PromoCodeWidgetMobile = ({
  availablePromoCodes,
}: IPromoCodeWidgetMobileProps) => {
  const [promoCode, setPromoCode] = useState<string>('');
  const [showInputWidget, setInputWidget] = useState<boolean>(false);
  const [hidePromoCodes, setHidePromoCodes] = useState<boolean>(false);
  const [disabledPromoCodes, setDisabledPromoCodes] = useState<boolean>(false);

  const promoCodeInputRef = useRef<HTMLInputElement>(null);
  const {
    promoCodeWidgetData,
    onPromoCodeApply,
    resetPromocode,
    appliedPromotionsData,
    promoCodesWithTriggeringRule,
  } = usePromotionsContext();
  const intl = useIntl();

  const { promoVerificationStatus, appliedPromoCode } = promoCodeWidgetData;
  const isLoading =
    promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.LOADING;
  const isInvalid =
    promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.INVALID;
  const isError =
    promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.ERROR;

  const widgetTexts = useMemo(() => {
    return {
      headerText: intl.formatMessage({ id: 'promotion.have_promo_code' }),
      placeholderText: intl.formatMessage({
        id: 'promotion.enter_promo_code_placeholder',
      }),
    };
  }, [intl]);

  useEffect(() => {
    const appliedPromotions = appliedPromotionsData?.appliedPromotions || [];
    if (appliedPromotions.length) {
      setDisabledPromoCodes(true);
    } else {
      setDisabledPromoCodes(false);
    }
  }, [appliedPromotionsData.appliedPromotions]);

  useEffect(() => {
    if (promoCodeInputRef.current) {
      promoCodeInputRef.current.focus();
    }
  }, [showInputWidget]);

  useEffect(() => {
    if (appliedPromoCode !== '') {
      setHidePromoCodes(true);
    } else {
      setHidePromoCodes(false);
    }
  }, [appliedPromoCode]);

  useEffect(() => {
    //disable promo codes to prevent double click
    if (promoVerificationStatus === PROMO_CODE_VERIFICATION_STATUS.LOADING) {
      setDisabledPromoCodes(true);
    } else {
      setDisabledPromoCodes(false);
    }
  }, [promoVerificationStatus]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value);
  };

  const handlePromoClick = (code: promoCodeType) => {
    if (!code.condition_fullfilled || disabledPromoCodes) {
      return;
    }
    onPromoCodeApply(code.code);
  };

  const handleCodeSubmit = () => {
    if (disabledPromoCodes) {
      return;
    }
    if (promoCode) {
      onPromoCodeApply(promoCode);
    }
  };

  const removePromocode = () => {
    setPromoCode('');
    resetPromocode();
  };

  const appliedPromoCodeText = useMemo(() => {
    const promoCode = availablePromoCodes.find(
      (code) => code.code === appliedPromoCode
    );
    return {
      code: promoCode?.code || appliedPromoCode || '',
      name: promoCode?.name || '',
      condition_fullfilled: promoCode?.condition_fullfilled || false,
    };
  }, [appliedPromoCode, availablePromoCodes]);

  const sortedPromoCodes = useMemo(() => {
    return [...promoCodesWithTriggeringRule].sort(
      (a, b) => Number(b.condition_fullfilled) - Number(a.condition_fullfilled)
    );
  }, [promoCodesWithTriggeringRule]);

  const handleToggleInputWidget = () => {
    if (disabledPromoCodes) {
      return;
    }
    setInputWidget(true);
  };

  // Early return if promo code is applied
  if (appliedPromoCode !== '') {
    return (
      <div
        className='promo-code-widget-mobile applied align-items-center'
        key={'applied-banner'}
      >
        <div className='applied-promocode d-flex align-items-center justify-content-between'>
          <div className='promo-info d-flex'>
            <div className='promo-icon mr-2'>
              <img src={promotionCoupon} alt='promotion coupon' />
            </div>
            <div className='d-flex flex-column'>
              <div className='promocode-name'>
                <span className='code'>{appliedPromoCodeText.code}</span>{' '}
                <FormattedMessage
                  id='promotion.promo_code_applied'
                  defaultMessage='applied'
                />
              </div>
              <div className='promo-description'>
                {appliedPromoCodeText.name}
              </div>
            </div>
          </div>
          <div className='remove-promo-button' onClick={removePromocode}>
            <FormattedMessage id='promotion.remove' defaultMessage='Remove' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='promo-code-widget-mobile' key={'promo-codes-list'}>
      {sortedPromoCodes.length ? (
        <div>
          <p className='input-label bold mb-0'>
            <FormattedMessage
              id='promotion.select_promo_code'
              defaultMessage='Select Promo Code'
            />
          </p>
        </div>
      ) : null}
      {!hidePromoCodes && sortedPromoCodes.length ? (
        <div className='promo-codes-list'>
          {sortedPromoCodes.map((code) => (
            <div
              className={`coupon-wrapper ${
                code.condition_fullfilled && !disabledPromoCodes
                  ? ''
                  : 'disabled'
              }`}
              onClick={() => handlePromoClick(code)}
              key={code.id}
            >
              <div className='coupon'>
                <div className='code'>{code.code}</div>
                <div className='discount'>{code.name}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {showInputWidget ? (
        <div className='promo-input-area'>
          <input
            ref={promoCodeInputRef}
            type='text'
            value={promoCode}
            onChange={handleInputChange}
            placeholder={widgetTexts.placeholderText}
            disabled={isLoading}
            className='promo-input'
          />
          <button
            className='button btn btn-primary promo-apply-button'
            onClick={handleCodeSubmit}
            disabled={isLoading || promoCode === ''}
          >
            {isLoading ? (
              <FormattedMessage
                id='promotion.applying'
                defaultMessage='Applying...'
              />
            ) : (
              <FormattedMessage id='global.apply' defaultMessage='Apply' />
            )}
          </button>
        </div>
      ) : (
        <div className='promotion-input d-flex justify-content-between'>
          <div>{widgetTexts.headerText}</div>
          <div
            className={`toggle-link ${disabledPromoCodes ? 'disabled' : ''}`}
            onClick={handleToggleInputWidget}
          >
            <FormattedMessage
              id='promotion.enter_promo_code'
              defaultMessage='Enter Code'
            />
          </div>
        </div>
      )}

      {isInvalid && (
        <div className='error-message'>
          <FormattedMessage
            id='promotion.invalid_promo_code'
            defaultMessage='Promo Code Not Available'
          />
        </div>
      )}
      {isError && (
        <div className='error-message'>
          <FormattedMessage
            id='promotion.error_applying_promo_code'
            defaultMessage='Error applying promo code!'
          />
        </div>
      )}
    </div>
  );
};

export default PromoCodeWidgetMobile;
